import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Save,
  Loader2,
  Palette,
  TrendingUp,
  Clock,
  Trophy,
  XCircle,
  CheckCircle,
  FileText
} from 'lucide-react';
import {
  FunnelStage,
  FunnelStageFormData,
  createFunnelStage,
  updateFunnelStage
} from '@/actions/data-fetch-funnel';
import { toastMessage } from '@/utils/toast-message';

const FunnelStageFormSchema = z.object({
  stage_name: z.string().min(1, 'Stage name is required').max(100, 'Name must be less than 100 characters'),
  stage_description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  stage_order: z.number().min(0, 'Order must be positive').int('Order must be an integer'),
  color_code: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format').optional(),
  funnel_type: z.enum(['sales', 'marketing', 'support', 'custom']).optional(),
  is_default: z.boolean().optional(),
  is_win: z.boolean().optional(),
  is_lost: z.boolean().optional(),
  probability_percentage: z.number().min(0).max(100).optional().nullable(),
  sla_hours: z.number().min(0).optional().nullable()
});

type FunnelStageFormFields = z.infer<typeof FunnelStageFormSchema>;

interface FunnelStageFormProps {
  stage: FunnelStage | null;
  onSuccess: (stage: FunnelStage) => void;
  onCancel: () => void;
}

const predefinedColors = [
  '#ea580c', // Brand
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#f97316', // Orange
  '#6366f1', // Indigo
];

const FunnelStageForm: React.FC<FunnelStageFormProps> = ({
  stage,
  onSuccess,
  onCancel
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColor, setSelectedColor] = useState(stage?.color_code || predefinedColors[0]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<FunnelStageFormFields>({
    resolver: zodResolver(FunnelStageFormSchema),
    defaultValues: {
      stage_name: stage?.stage_name || '',
      stage_description: stage?.stage_description || '',
      stage_order: stage?.stage_order || 0,
      color_code: stage?.color_code || predefinedColors[0],
      funnel_type: (stage?.funnel_type || 'sales') as 'sales' | 'marketing' | 'support' | 'custom',
      is_default: stage?.is_default || false,
      is_win: stage?.is_win || false,
      is_lost: stage?.is_lost || false,
      probability_percentage: stage?.probability_percentage || null,
      sla_hours: stage?.sla_hours || null
    }
  });

  const isWin = watch('is_win');
  const isLost = watch('is_lost');

  useEffect(() => {
    if (stage?.color_code) {
      setSelectedColor(stage.color_code);
    }
  }, [stage]);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    setValue('color_code', color);
  };

  const onSubmit = async (data: FunnelStageFormFields) => {
    try {
      setIsSubmitting(true);

      // Prepare data
      const formData: FunnelStageFormData = {
        stage_name: data.stage_name,
        stage_description: data.stage_description,
        stage_order: data.stage_order,
        color_code: selectedColor,
        funnel_type: data.funnel_type,
        is_default: data.is_default,
        is_win: data.is_win,
        is_lost: data.is_lost,
        probability_percentage: data.probability_percentage || undefined,
        sla_hours: data.sla_hours || undefined
      };

      let response;
      if (stage?.stage_id) {
        response = await updateFunnelStage(stage.stage_id, formData);
      } else {
        response = await createFunnelStage(formData);
      }

      if (response.success && response.data?.data) {
        onSuccess(response.data.data);
      } else {
        throw new Error(response.message || 'Failed to save funnel stage');
      }
    } catch (error: any) {
      console.error('Error submitting funnel stage:', error);
      toastMessage(
        error.message || `Failed to ${stage ? 'update' : 'create'} funnel stage`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Basic Information
          </h3>

          {/* Stage Name */}
          <div>
            <label htmlFor="stage_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stage Name <span className="text-red-500">*</span>
            </label>
            <input
              id="stage_name"
              type="text"
              {...register('stage_name')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              placeholder="e.g., Lead Qualification"
            />
            {errors.stage_name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.stage_name.message}</p>
            )}
          </div>

          {/* Stage Description */}
          <div>
            <label htmlFor="stage_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              id="stage_description"
              {...register('stage_description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              placeholder="Describe this funnel stage..."
            />
            {errors.stage_description && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.stage_description.message}</p>
            )}
          </div>

          {/* Stage Order and Funnel Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="stage_order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Order <span className="text-red-500">*</span>
              </label>
              <input
                id="stage_order"
                type="number"
                {...register('stage_order', { valueAsNumber: true })}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              />
              {errors.stage_order && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.stage_order.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="funnel_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Funnel Type
              </label>
              <select
                id="funnel_type"
                {...register('funnel_type')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              >
                <option value="sales">Sales</option>
                <option value="marketing">Marketing</option>
                <option value="support">Support</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
        </div>

        {/* Visual Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Visual Configuration
          </h3>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Stage Color
            </label>
            <div className="flex items-center space-x-3">
              <div
                className="w-12 h-12 rounded-lg border-2 border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: selectedColor }}
              ></div>
              <div className="flex flex-wrap gap-2">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorChange(color)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      selectedColor === color
                        ? 'border-gray-900 dark:border-white scale-110'
                        : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  ></button>
                ))}
              </div>
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="w-12 h-12 rounded-lg cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Stage Properties */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Stage Properties
          </h3>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-brand-900 dark:text-brand-300 mb-2">
              Understanding Stage Properties
            </h4>
            <ul className="space-y-1 text-sm text-brand-800 dark:text-brand-400">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Probability %:</strong> The likelihood of conversion at this stage (e.g., "Qualified" = 30%, "Proposal Sent" = 60%, "Negotiation" = 80%)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>SLA Hours:</strong> Maximum time a lead should stay in this stage before action is required (triggers alerts/notifications)</span>
              </li>
            </ul>
          </div>

          {/* Probability Percentage */}
          <div>
            <label htmlFor="probability_percentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Probability Percentage (%)
            </label>
            <input
              id="probability_percentage"
              type="number"
              {...register('probability_percentage', { valueAsNumber: true, setValueAs: (v) => v === '' ? null : Number(v) })}
              min="0"
              max="100"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              placeholder="e.g., 75"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              💡 Higher stages typically have higher probabilities (e.g., initial contact = 10%, demo scheduled = 50%, contract sent = 80%)
            </p>
            {errors.probability_percentage && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.probability_percentage.message}</p>
            )}
          </div>

          {/* SLA Hours */}
          <div>
            <label htmlFor="sla_hours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              SLA Hours (Service Level Agreement)
            </label>
            <input
              id="sla_hours"
              type="number"
              {...register('sla_hours', { valueAsNumber: true, setValueAs: (v) => v === '' ? null : Number(v) })}
              min="0"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              placeholder="e.g., 24"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              ⏰ Set time limits to ensure leads don't get stuck (e.g., "New Lead" = 4 hours, "Follow-up" = 24 hours, "Proposal" = 72 hours)
            </p>
            {errors.sla_hours && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sla_hours.message}</p>
            )}
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('is_default')}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1 text-purple-500" />
                Set as default stage
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('is_win')}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <Trophy className="w-4 h-4 mr-1 text-green-500" />
                Mark as win stage (successful conversion)
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('is_lost')}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <XCircle className="w-4 h-4 mr-1 text-red-500" />
                Mark as lost stage (failed conversion)
              </span>
            </label>
          </div>

          {(isWin || isLost) && (
            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-400">
                ⚠️ {isWin && isLost ? 'A stage cannot be both Win and Lost at the same time.' :
                     isWin ? 'This stage will be marked as a successful conversion endpoint.' :
                     'This stage will be marked as a failed conversion endpoint.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {stage ? 'Update Stage' : 'Create Stage'}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default FunnelStageForm;
